"use client";

import { useCallback, useRef, useState } from "react";
import { toast } from "@/lib/toast";
import { MAX_UPLOAD_SIZE_MB, fileTooLargeMessage } from "@/lib/upload-limits";
import { blobToFile, type CropOutputOptions } from "@/lib/cropImage";
import type { ImageCropModalProps } from "@/components/tailwind/ui/ImageCropModal";

export interface UseImageCropUploadOptions {
  /** Uploads the cropped file and resolves to its URL. */
  upload: (file: File) => Promise<string>;
  /** Called with the uploaded URL after a successful upload. */
  onUploaded?: (url: string) => void | Promise<void>;
  aspect?: number;
  cropShape?: "rect" | "round";
  title?: string;
  output?: CropOutputOptions;
  /** File name for the generated upload. Default derived from the output type. */
  fileName?: string;
  /** Max accepted source size in MB. Default 5. */
  maxSizeMB?: number;
}

/** Props to spread directly onto <ImageCropModal />. */
type CropModalProps = Pick<
  ImageCropModalProps,
  "open" | "imageSrc" | "aspect" | "cropShape" | "title" | "isProcessing" | "output" | "onCancel" | "onConfirm"
>;

export interface UseImageCropUpload {
  /** Validate a picked file and open the cropper for it. */
  openCropper: (file: File) => void;
  isUploading: boolean;
  cropModalProps: CropModalProps;
}

/**
 * Reusable pick -> crop -> upload pipeline. Pair with <ImageCropModal /> by
 * spreading `cropModalProps`. Upload feedback (success/error toasts) is left to
 * the provided `upload` fn so it isn't duplicated; only client-side validation
 * errors are surfaced here.
 */
export function useImageCropUpload(options: UseImageCropUploadOptions): UseImageCropUpload {
  const {
    upload,
    onUploaded,
    aspect = 1,
    cropShape = "round",
    title,
    output,
    fileName,
    maxSizeMB = MAX_UPLOAD_SIZE_MB,
  } = options;

  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const objectUrlRef = useRef<string | null>(null);

  const cleanup = useCallback(() => {
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
    setImageSrc(null);
    setOpen(false);
  }, []);

  const openCropper = useCallback(
    (file: File) => {
      if (!file.type.includes("image/")) {
        toast.error("File type not supported. Please upload an image file.");
        return;
      }
      if (file.size / 1024 / 1024 > maxSizeMB) {
        toast.error(fileTooLargeMessage(maxSizeMB));
        return;
      }
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
      const url = URL.createObjectURL(file);
      objectUrlRef.current = url;
      setImageSrc(url);
      setOpen(true);
    },
    [maxSizeMB],
  );

  const onCancel = useCallback(() => {
    cleanup();
  }, [cleanup]);

  const onConfirm = useCallback(
    async (blob: Blob) => {
      try {
        setIsUploading(true);
        const subtype = (output?.mimeType || blob.type || "image/jpeg").split("/")[1] || "jpg";
        const ext = subtype === "jpeg" ? "jpg" : subtype;
        const name = fileName || `image.${ext}`;
        const file = blobToFile(blob, name);
        const url = await upload(file);
        await onUploaded?.(url);
        cleanup();
      } catch (e) {
        console.error("Crop upload failed", e);
      } finally {
        setIsUploading(false);
      }
    },
    [upload, onUploaded, cleanup, fileName, output?.mimeType],
  );

  return {
    openCropper,
    isUploading,
    cropModalProps: {
      open,
      imageSrc,
      aspect,
      cropShape,
      title,
      isProcessing: isUploading,
      output,
      onCancel,
      onConfirm,
    },
  };
}
